import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { 
  Mail, 
  Phone, 
  MapPin, 
  Clock,
  Send,
  MessageSquare,
  Building2,
  Globe
} from 'lucide-react';
import { toast } from 'sonner';
import { contentService } from '@/services/api';

const ContactPage = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language || 'ar';
  const isRTL = lang === 'ar';

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [faqs, setFaqs] = useState([]);

  // Fetch settings and FAQs on mount
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch settings
        const settingsResponse = await contentService.getSettings();
        if (settingsResponse.data?.data) {
          setSettings(settingsResponse.data.data);
        }
        
        // Fetch FAQs
        try {
          const faqsResponse = await contentService.getFaqs();
          if (faqsResponse.data?.data && Array.isArray(faqsResponse.data.data)) {
            setFaqs(faqsResponse.data.data.slice(0, 5)); // Get first 5 FAQs
          }
        } catch (faqErr) {
          console.log('FAQs not available, using defaults');
        }
      } catch (err) {
        console.error('Error fetching settings:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    // In a real implementation, this would POST to a contact endpoint
    // For now, we simulate the submission
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    toast.success(lang === 'ar' ? 'تم إرسال رسالتك بنجاح!' : 'Your message has been sent successfully!');
    setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
    setSubmitting(false);
  };

  // Default contact info (can be overridden by settings)
  const contactInfo = [
    {
      icon: Phone,
      title: lang === 'ar' ? 'اتصل بنا' : 'Call Us',
      details: ['+963 11 123 4567', '+963 933 123 456'],
      color: 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400'
    },
    {
      icon: Mail,
      title: lang === 'ar' ? 'راسلنا' : 'Email Us',
      details: ['contact@dactrha.com', 'support@dactrha.com'],
      color: 'bg-teal-100 text-teal-600 dark:bg-teal-900/30 dark:text-teal-400'
    },
    {
      icon: MapPin,
      title: lang === 'ar' ? 'موقعنا' : 'Our Location',
      details: [lang === 'ar' ? 'دمشق، سوريا' : 'Damascus, Syria', lang === 'ar' ? 'شارع الحمراء، بناء رقم 15' : 'Al-Hamra Street, Building 15'],
      color: 'bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400'
    },
    {
      icon: Clock,
      title: lang === 'ar' ? 'ساعات العمل' : 'Working Hours',
      details: [lang === 'ar' ? 'الأحد - الخميس: 9 صباحاً - 6 مساءً' : 'Sun - Thu: 9 AM - 6 PM', lang === 'ar' ? 'الجمعة - السبت: مغلق' : 'Fri - Sat: Closed'],
      color: 'bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400'
    },
  ];

  // Default FAQs if API doesn't return any
  const defaultFaqs = [
    {
      q: lang === 'ar' ? 'كيف يمكنني حجز موعد؟' : 'How can I book an appointment?',
      a: lang === 'ar' ? 'يمكنك حجز موعد بسهولة من خلال البحث عن طبيب واختيار الوقت المناسب' : 'You can easily book an appointment by searching for a doctor and selecting a suitable time'
    },
    {
      q: lang === 'ar' ? 'هل الاستشارات عبر الإنترنت متاحة؟' : 'Are online consultations available?',
      a: lang === 'ar' ? 'نعم، نوفر استشارات طبية عبر الفيديو مع أطباء معتمدين' : 'Yes, we provide video medical consultations with certified doctors'
    },
    {
      q: lang === 'ar' ? 'كيف يمكنني إلغاء أو تغيير موعدي؟' : 'How can I cancel or change my appointment?',
      a: lang === 'ar' ? 'يمكنك إدارة مواعيدك من خلال قسم "مواعيدي" في حسابك' : 'You can manage your appointments through the "My Appointments" section in your account'
    },
  ];

  const displayFaqs = faqs.length > 0 ? faqs.map(faq => ({
    q: lang === 'ar' ? (faq.question_ar || faq.question) : (faq.question_en || faq.question),
    a: lang === 'ar' ? (faq.answer_ar || faq.answer) : (faq.answer_en || faq.answer)
  })) : defaultFaqs;

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900" dir={isRTL ? 'rtl' : 'ltr'} data-testid="contact-page">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-teal-600 to-teal-700 text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <Badge className="bg-white/20 text-white mb-4">
            {lang === 'ar' ? 'تواصل معنا' : 'Contact Us'}
          </Badge>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            {lang === 'ar' ? 'نحن هنا لمساعدتك' : "We're Here to Help"}
          </h1>
          <p className="text-xl text-teal-100 max-w-2xl mx-auto">
            {lang === 'ar' 
              ? 'لديك سؤال أو استفسار؟ فريقنا جاهز لمساعدتك على مدار الساعة'
              : 'Have a question or inquiry? Our team is ready to help you 24/7'}
          </p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">
        {/* Contact Info Cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 -mt-16 mb-12">
          {loading ? (
            Array.from({ length: 4 }).map((_, idx) => (
              <Card key={idx} className="dark:bg-slate-800 border-0 shadow-lg">
                <CardContent className="p-6">
                  <Skeleton className="w-12 h-12 rounded-full mb-4" />
                  <Skeleton className="h-5 w-1/2 mb-2" />
                  <Skeleton className="h-4 w-3/4 mb-1" />
                  <Skeleton className="h-4 w-2/3" />
                </CardContent>
              </Card>
            ))
          ) : (
            contactInfo.map((info, idx) => (
              <Card key={idx} className="dark:bg-slate-800 border-0 shadow-lg">
                <CardContent className="p-6">
                  <div className={`w-12 h-12 rounded-full ${info.color} flex items-center justify-center mb-4`}>
                    <info.icon className="h-6 w-6" />
                  </div>
                  <h3 className="font-semibold text-slate-900 dark:text-white mb-2">{info.title}</h3>
                  {info.details.map((detail, i) => (
                    <p key={i} className="text-sm text-slate-600 dark:text-slate-400">{detail}</p>
                  ))}
                </CardContent>
              </Card>
            ))
          )}
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <Card className="dark:bg-slate-800 border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageSquare className="h-5 w-5 text-teal-600" />
                {lang === 'ar' ? 'أرسل لنا رسالة' : 'Send Us a Message'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">{lang === 'ar' ? 'الاسم الكامل' : 'Full Name'}</Label>
                    <Input
                      id="name"
                      data-testid="contact-name"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      placeholder={lang === 'ar' ? 'أدخل اسمك' : 'Enter your name'}
                      required
                      className="dark:bg-slate-900"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">{lang === 'ar' ? 'البريد الإلكتروني' : 'Email'}</Label>
                    <Input
                      id="email"
                      type="email"
                      data-testid="contact-email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      placeholder={lang === 'ar' ? 'أدخل بريدك الإلكتروني' : 'Enter your email'}
                      required
                      className="dark:bg-slate-900"
                    />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="phone">{lang === 'ar' ? 'رقم الهاتف' : 'Phone Number'}</Label>
                    <Input
                      id="phone"
                      data-testid="contact-phone"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      placeholder={lang === 'ar' ? 'أدخل رقم هاتفك' : 'Enter your phone'}
                      className="dark:bg-slate-900"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="subject">{lang === 'ar' ? 'الموضوع' : 'Subject'}</Label>
                    <Input
                      id="subject"
                      data-testid="contact-subject"
                      value={formData.subject}
                      onChange={(e) => setFormData({...formData, subject: e.target.value})}
                      placeholder={lang === 'ar' ? 'موضوع الرسالة' : 'Message subject'}
                      required
                      className="dark:bg-slate-900"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="message">{lang === 'ar' ? 'الرسالة' : 'Message'}</Label>
                  <Textarea
                    id="message"
                    data-testid="contact-message"
                    value={formData.message}
                    onChange={(e) => setFormData({...formData, message: e.target.value})}
                    placeholder={lang === 'ar' ? 'اكتب رسالتك هنا...' : 'Write your message here...'}
                    rows={5}
                    required
                    className="dark:bg-slate-900"
                  />
                </div>
                <Button 
                  type="submit" 
                  className="w-full bg-teal-600 hover:bg-teal-700" 
                  disabled={submitting}
                  data-testid="contact-submit"
                >
                  {submitting ? (
                    <span className="flex items-center gap-2">
                      <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></span>
                      {lang === 'ar' ? 'جاري الإرسال...' : 'Sending...'}
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <Send className="h-4 w-4" />
                      {lang === 'ar' ? 'إرسال الرسالة' : 'Send Message'}
                    </span>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* FAQ Section */}
          <div>
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-6">
              {lang === 'ar' ? 'الأسئلة الشائعة' : 'Frequently Asked Questions'}
            </h2>
            <div className="space-y-4">
              {displayFaqs.map((faq, idx) => (
                <Card key={idx} className="dark:bg-slate-800 border-0 shadow-sm">
                  <CardContent className="p-4">
                    <h3 className="font-semibold text-slate-900 dark:text-white mb-2">{faq.q}</h3>
                    <p className="text-sm text-slate-600 dark:text-slate-400">{faq.a}</p>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Map Placeholder */}
            <Card className="dark:bg-slate-800 border-0 shadow-sm mt-6 overflow-hidden">
              <div className="h-64 bg-slate-200 dark:bg-slate-700 flex items-center justify-center">
                <div className="text-center">
                  <MapPin className="h-12 w-12 text-slate-400 mx-auto mb-2" />
                  <p className="text-slate-500 dark:text-slate-400">
                    {lang === 'ar' ? 'دمشق، سوريا' : 'Damascus, Syria'}
                  </p>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
